import { NetCore, type NetCoreHandlers } from '@platoon/client-kit';
import {
  INPUT_SIZE,
  MSG_HIT,
  MSG_MATCH,
  MSG_SHOT,
  MSG_SNAPSHOT,
  START_SIZE,
  encodeStart,
  PROTOCOL_VERSION,
  SETPROP_SIZE,
  decodeHit,
  decodeMatch,
  decodeShot,
  encodeInput,
  encodeSetProp,
  readSnapshotEntry,
  readSnapshotHeader,
  readSnapshotPickup,
  readSnapshotPickupCount,
  type HitMsg,
  type MatchMsg,
  type ShotMsg,
  type SnapshotEntry,
  type SnapshotHeader,
  type SnapshotPickup,
} from '@mayday/shared';

export interface NetHandlers extends NetCoreHandlers {
  onShot(s: ShotMsg): void;
  onHit(h: HitMsg): void;
  onMatch(m: MatchMsg): void;
  onSnapshotBegin(tick: number): void;
  onSnapshotEntry(e: SnapshotEntry): void;
  onSnapshotPickup(p: SnapshotPickup): void;
  onSnapshotEnd(): void;
}

// Mayday's messages on top of the kit's NetCore (welcome, events, ping,
// roster, resume tokens live there). Decoders write into reused objects.
export class Net extends NetCore {
  private readonly inputBuf = new ArrayBuffer(INPUT_SIZE);
  private readonly inputView = new DataView(this.inputBuf);
  private readonly header: SnapshotHeader = { tick: 0, count: 0 };
  private readonly entry: SnapshotEntry = {
    id: 0, x: 0, y: 0, aim: 0, lastSeq: 0, health: 0, ammo: 0, reserve: 0, cooldown: 0, reload: 0, invuln: 0,
    disguise: 0, camoTicks: 0, camoProp: 0, weapon: 0, switchTicks: 0, grenades: 0, throwCooldown: 0,
  };
  private readonly shot: ShotMsg = { owner: 0, x: 0, y: 0, angle: 0, tick: 0, kind: 0, dist: 0 };
  private readonly hit: HitMsg = { shooter: 0, victim: 0, damage: 0 };
  private readonly match: MatchMsg = { state: 0, winner: 0, killLimit: 0, timeLimitS: 0, secondsLeft: 0, levelHash: 0, mode: 0, friendlyFire: false, winnerTeam: 0, teams: 0, teamSize: 0 };
  private readonly pickup: SnapshotPickup = { slot: 0, kind: 0, x: 0, y: 0 };

  constructor(private readonly handlers: NetHandlers) {
    super(PROTOCOL_VERSION, handlers);
  }

  sendInput(seq: number, buttons: number, aim: number, throwDist = 0): void {
    const len = encodeInput(this.inputView, seq, buttons, aim, throwDist);
    this.send(new Uint8Array(this.inputBuf, 0, len));
  }

  // Warm-up: "start the match now".
  sendStart(): void {
    const buf = new ArrayBuffer(START_SIZE);
    this.send(new Uint8Array(buf, 0, encodeStart(new DataView(buf))));
  }

  sendProp(prop: number): void {
    const buf = new ArrayBuffer(SETPROP_SIZE);
    const len = encodeSetProp(new DataView(buf), prop);
    this.send(new Uint8Array(buf, 0, len));
  }

  protected onGameMessage(type: number, view: DataView): void {
    switch (type) {
      case MSG_SHOT:
        if (decodeShot(view, this.shot)) this.handlers.onShot(this.shot);
        break;
      case MSG_HIT:
        if (decodeHit(view, this.hit)) this.handlers.onHit(this.hit);
        break;
      case MSG_MATCH:
        if (decodeMatch(view, this.match)) this.handlers.onMatch(this.match);
        break;
      case MSG_SNAPSHOT: {
        const h = this.header;
        if (!readSnapshotHeader(view, h)) return;
        this.handlers.onSnapshotBegin(h.tick);
        for (let i = 0; i < h.count; i++) {
          readSnapshotEntry(view, i, this.entry);
          this.handlers.onSnapshotEntry(this.entry);
        }
        const pickups = readSnapshotPickupCount(view, h.count);
        for (let i = 0; i < pickups; i++) {
          readSnapshotPickup(view, h.count, i, this.pickup);
          this.handlers.onSnapshotPickup(this.pickup);
        }
        this.handlers.onSnapshotEnd();
        break;
      }
      default:
        break; // unknown types are ignored
    }
  }
}
